import React from 'react';
import { motion } from 'framer-motion';
import { ThermometerIcon, ZapIcon, WindIcon, MoonIcon, ArrowLeftIcon, AlertCircleIcon, CheckCircle2Icon, ActivityIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
interface ZoneDetailSidebarProps {
  room: Room;
  onClose: () => void;
}
export function ZoneDetailSidebar({
  room,
  onClose
}: ZoneDetailSidebarProps) {
  // Mock status data based on room
  const getStatus = (id: string) => {
    switch (id) {
      case 'living':
        return {
          comfort: '쾌적함',
          power: '정상',
          co2: '450ppm (좋음)',
          alerts: []
        };
      case 'bedroom':
        return {
          comfort: '쾌적성 저하',
          power: '전력소모 과다',
          co2: '800ppm (보통)',
          alerts: ['온도가 설정보다 높습니다', '에어컨 효율 저하 감지']
        };
      case 'kitchen':
        return {
          comfort: '보통',
          power: '정상',
          co2: '950ppm (환기 필요)',
          alerts: ['이산화탄소 농도 증가']
        };
      default:
        return {
          comfort: '쾌적함',
          power: '정상',
          co2: '400ppm (좋음)',
          alerts: []
        };
    }
  };
  const status = getStatus(room.id);
  const hasAlerts = status.alerts.length > 0;
  return <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-399">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 mb-2" data-id="element-400">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-401">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-402" />
        </button>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500" data-id="element-403">
          Zone Detail
        </div>
      </div>

      {/* Main Room Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="glass-panel rounded-2xl p-6 relative overflow-hidden" data-id="element-404">
        <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-405">
          <room.icon className="w-24 h-24 text-white" data-id="element-406" />
        </div>

        <h2 className="text-2xl font-bold text-white mb-1" data-id="element-407">{room.name}</h2>
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-6" data-id="element-408">
          <span className={`w-2 h-2 rounded-full ${hasAlerts ? 'bg-amber-400' : 'bg-emerald-400'} animate-pulse`} data-id="element-409" />
          <span data-id="element-410">{hasAlerts ? 'Attention Needed' : 'Operating Normally'}</span>
        </div>

        <div className="space-y-4" data-id="element-411">
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-412">
            <div className="flex items-center gap-3" data-id="element-413">
              <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400" data-id="element-414">
                <ThermometerIcon className="w-4 h-4" data-id="element-415" />
              </div>
              <div data-id="element-416">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-417">
                  Comfort
                </div>
                <div className="text-sm font-medium text-slate-200" data-id="element-418">
                  {status.comfort}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-419">
            <div className="flex items-center gap-3" data-id="element-420">
              <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400" data-id="element-421">
                <ZapIcon className="w-4 h-4" data-id="element-422" />
              </div>
              <div data-id="element-423">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-424">
                  Power
                </div>
                <div className="text-sm font-medium text-slate-200" data-id="element-425">
                  {status.power}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-426">
            <div className="flex items-center gap-3" data-id="element-427">
              <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400" data-id="element-428">
                <WindIcon className="w-4 h-4" data-id="element-429" />
              </div>
              <div data-id="element-430">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-431">
                  Air Quality
                </div>
                <div className="text-sm font-medium text-slate-200" data-id="element-432">
                  {status.co2}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Alerts Section */}
      {hasAlerts && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="glass-panel rounded-2xl p-4 border-l-4 border-l-amber-500" data-id="element-433">
          <div className="flex items-center gap-2 mb-3 text-amber-400" data-id="element-434">
            <AlertCircleIcon className="w-4 h-4" data-id="element-435" />
            <span className="text-xs font-bold uppercase tracking-wider" data-id="element-436">
              System Alerts
            </span>
          </div>
          <div className="space-y-2" data-id="element-437">
            {status.alerts.map((alert, i) => <div key={i} className="text-sm text-slate-300 flex items-start gap-2" data-id="element-438">
                <span className="w-1 h-1 rounded-full bg-amber-500 mt-2 flex-shrink-0" data-id="element-439" />
                {alert}
              </div>)}
          </div>
        </motion.div>}

      {/* Sleep Care Action */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="mt-auto" data-id="element-440">
        <button className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 p-4 transition-all hover:scale-[1.02] active:scale-[0.98]" data-id="element-441">
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-442" />
          <div className="relative flex items-center justify-between" data-id="element-443">
            <div className="flex items-center gap-3" data-id="element-444">
              <div className="p-2 rounded-lg bg-white/20 text-white" data-id="element-445">
                <MoonIcon className="w-5 h-5" data-id="element-446" />
              </div>
              <div className="text-left" data-id="element-447">
                <div className="text-sm font-bold text-white" data-id="element-448">
                  수면케어 모드
                </div>
                <div className="text-[10px] text-indigo-200" data-id="element-449">
                  최적의 수면 환경 설정하기
                </div>
              </div>
            </div>
            <ArrowLeftIcon className="w-5 h-5 text-white rotate-180" data-id="element-450" />
          </div>
        </button>
      </motion.div>
    </div>;
}